/**
 * Утилиты для работы с датами
 */

/**
 * Форматирует дату для отображения пользователю
 * @param date - Дата в формате строки (YYYY-MM-DD) или объект Date
 * @param locale - Локаль для форматирования (по умолчанию 'ru-RU')
 * @returns Отформатированная дата
 */
export const formatDate = (date: string | Date, locale = 'ru-RU'): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (Number.isNaN(dateObj.getTime())) {
    return date.toString();
  }

  return dateObj.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

/**
 * Форматирует дату с днём недели для отображения
 * @param date - Дата в формате строки (YYYY-MM-DD) или объект Date
 * @param locale - Локаль для форматирования (по умолчанию 'ru-RU')
 * @returns Отформатированная дата с днём недели
 */
export const formatDateWithWeekday = (date: string | Date, locale = 'ru-RU'): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (Number.isNaN(dateObj.getTime())) {
    return date.toString();
  }

  return dateObj.toLocaleDateString(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

/**
 * Форматирует дату и время для отображения
 * @param date - Дата в формате строки или объект Date
 * @param locale - Локаль для форматирования (по умолчанию 'ru-RU')
 * @returns Отформатированная дата и время
 */
export const formatDateTime = (date: string | Date, locale = 'ru-RU'): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (Number.isNaN(dateObj.getTime())) {
    return date.toString();
  }

  return dateObj.toLocaleString(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Проверяет, является ли дата валидной
 * @param date - Дата для проверки
 * @returns true, если дата валидна
 */
export const isValidDate = (date: string | Date): boolean => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return !Number.isNaN(dateObj.getTime());
};

/**
 * Проверяет, находится ли дата в будущем
 * @param date - Дата для проверки
 * @returns true, если дата в будущем
 */
export const isFutureDate = (date: string | Date): boolean => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const checkDate = new Date(dateObj);
  checkDate.setHours(0, 0, 0, 0);
  return checkDate >= today;
};

/**
 * Получает дату в формате YYYY-MM-DD для input[type="date"]
 * @param date - Дата для форматирования
 * @returns Дата в формате YYYY-MM-DD
 */
export const formatDateForInput = (date: string | Date): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (Number.isNaN(dateObj.getTime())) {
    return '';
  }

  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};
